import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Button,
  CircularProgress,
} from "@mui/material";
import { MdOutlineFileUpload } from "react-icons/md";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import CInput from "../../../components/CInput";
import { getApiData } from "../../../utils/APIHelper";
import { toast } from "react-toastify";

const UploadArea = ({ children, onClick, ...props }) => (
  <Box
    onClick={onClick}
    sx={{
      border: "2px dashed #D1D5DB",
      borderRadius: "8px",
      padding: "32px",
      textAlign: "center",
      cursor: "pointer",
      backgroundColor: "#F9FAFB",
      "&:hover": {
        backgroundColor: "#F3F4F6",
      },
      ...props.sx,
    }}
  >
    {children}
  </Box>
);

const ProposalResponse = ({
  proposalImages,
  proposalDescription,
  setProposalDescription,
  timeline,
  setTimeline,
  calculateTotalWeeks,
  handleProposalImageUpload,
  handleDeleteProposalImage,
  proposalId,
  isDisabled = false,
  hideSaveButton = false,
}) => {
  const [isSavingTimeline, setIsSavingTimeline] = useState(false);

  const validateTimeline = () => {
    const errors = [];

    // Only validate timeline fields that have values (not required)
    // Allow empty fields, 0, and positive integers
    Object.entries(timeline).forEach(([key, value]) => {
      const stringValue = String(value || "");
      if (stringValue.trim() !== "") {
        // Check if value is a non-negative integer (0 or positive)
        const numValue = parseInt(stringValue);
        if (isNaN(numValue) || numValue < 0 || !Number.isInteger(numValue)) {
          errors.push(`${key} must be a non-negative integer`);
        }
      }
    });

    return errors;
  };

  const handleSaveTimeline = async () => {
    const validationErrors = validateTimeline();

    if (validationErrors.length > 0) {
      validationErrors.forEach((error) => toast.error(error));
      return;
    }

    setIsSavingTimeline(true);

    try {
      const payload = {
        proposal_id: proposalId,
      };

      // Only include scope_of_work if it has a value
      if (proposalDescription && proposalDescription.trim() !== "") {
        payload.scope_of_work = proposalDescription;
      }

      // Only include timeline fields that have values (including 0)
      const timelineWithValues = Object.fromEntries(
        Object.entries(timeline)
          .filter(
            ([key, value]) =>
              value !== "" && value !== null && value !== undefined
          )
          .map(([key, value]) => [key, parseInt(value)])
      );

      if (Object.keys(timelineWithValues).length > 0) {
        payload.timeline = timelineWithValues;
      }

      const response = await getApiData("api/bid/update", "POST", payload);

      if (response.success) {
        toast.success(response.message);
      } else {
        toast.error("Failed to save proposal. Please try again.");
      }
    } catch (error) {
      console.error("Error saving proposal:", error);
      toast.error("Error saving proposal. Please try again.");
    } finally {
      setIsSavingTimeline(false);
    }
  };

  return (
    <Card
      sx={{
        borderRadius: "12px",
        boxShadow: "none",
        margin: "0",
        overflow: "initial",
      }}
    >
      <CardContent sx={{ padding: "0" }}>
        <div style={{ padding: "0", margin: "1vh 0" }}>
          {/* Top section — Project Images on the left (thumbnail grid
              with a "+" upload tile), Project Description & Approach on
              the right. They sit side-by-side on md+, stack on mobile. */}
          <Box
            sx={{
              display: "grid",
              // Image gets its intrinsic width (200px tile), description
              // fills the remaining row width so it sits flush next to
              // the image instead of jumping to a half-width column.
              gridTemplateColumns: { xs: "1fr", md: "auto 1fr" },
              gap: 2,
              alignItems: "stretch",
              marginBottom: "24px",
            }}
          >
            {/* LEFT — Project Images. Only the "primary" image (tag-less
                entry in project_image) is surfaced here; image present →
                thumbnail with a close (×) button to remove, otherwise a
                fixed-size "+" tile invites an upload. Both states share
                the same width/height so the layout doesn't jump. */}
            <Box>
              <Typography
                variant="subtitle1"
                sx={{
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#0d0d0e",
                  letterSpacing: "0",
                }}
              >
                Project Images
              </Typography>
              {(() => {
                const primary = (proposalImages || []).find(
                  (img) => img && img.tags == null,
                );
                const tileSx = {
                  width: 200,
                  height: 190,
                  borderRadius: "10px",
                  overflow: "hidden",
                  flexShrink: 0,
                };
                return (
                  <Box>
                    {primary ? (
                      <Box
                        sx={{
                          ...tileSx,
                          position: "relative",
                          border: "1px solid #E5E7EB",
                        }}
                      >
                        <img
                          src={primary.image || primary}
                          alt="Project"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                        {!isDisabled && (
                          <IconButton
                            size="small"
                            sx={{
                              position: "absolute",
                              top: 6,
                              right: 6,
                              padding: "2px",
                              backgroundColor: "#DC2626",
                              boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
                              "&:hover": {
                                backgroundColor: "#B91C1C",
                              },
                            }}
                            onClick={() =>
                              handleDeleteProposalImage(primary.id)
                            }
                          >
                            <CloseIcon
                              fontSize="small"
                              sx={{ color: "#fff" }}
                            />
                          </IconButton>
                        )}
                      </Box>
                    ) : (
                      <Box
                        role="button"
                        onClick={() =>
                          !isDisabled &&
                          document
                            .getElementById("proposal-image-upload")
                            ?.click()
                        }
                        sx={{
                          ...tileSx,
                          border: "1px dashed #D1D5DB",
                          backgroundColor: "#F9FAFB",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: isDisabled ? "not-allowed" : "pointer",
                          color: "#4D6BF6",
                          fontSize: 40,
                          lineHeight: 1,
                          fontWeight: 300,
                          "&:hover": {
                            backgroundColor: isDisabled
                              ? "#F9FAFB"
                              : "#F3F4F6",
                          },
                        }}
                      >
                        +
                      </Box>
                    )}
                    <input
                      id="proposal-image-upload"
                      type="file"
                      accept="image/*"
                      multiple
                      style={{ display: "none" }}
                      disabled={isDisabled}
                      onChange={handleProposalImageUpload}
                    />
                  </Box>
                );
              })()}
            </Box>

            {/* RIGHT — Project Description & Approach */}
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography
                variant="subtitle1"
                sx={{
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#0d0d0e",
                  letterSpacing: "0",
                }}
              >
                Project Description & Approach
              </Typography>
              <CInput
                multiline
                rows={7}
                placeholder="Describe your approach to this project..."
                value={proposalDescription}
                onChange={(e) => setProposalDescription(e.target.value)}
                disabled={isDisabled}
              />
            </Box>
          </Box>

          {/* Estimated Timeline Section */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              marginTop: "2vh",
              flexDirection: "column",
              gap: "0px",
              marginBottom: "16px",
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontSize: "14px",
                fontWeight: "500",
                color: "#0d0d0e",
                letterSpacing: "0",
                marginBottom: "1vh",
              }}
            >
              Estimated Timeline (in weeks)
            </Typography>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "10px",
                width: "100%",
              }}
            >
              {[
                "Approvals",
                "Demolition & Site Prep",
                "Construction",
                "Finishing & Handover",
              ].map((key) => (
                <div key={key} style={{ width: "100%" }}>
                  <CInput
                    label={key}
                    type="number"
                    inputProps={{ min: 0, step: 1 }}
                    placeholder={`e.g., ${
                      key === "Approvals"
                        ? "2"
                        : key === "Demolition & Site Prep"
                        ? "1"
                        : key === "Construction"
                        ? "8"
                        : "2"
                    }`}
                    value={timeline[key] || ""}
                    onChange={(e) => {
                      const inputValue = e.target.value;
                      // Only allow empty or non-negative integers
                      if (inputValue === "" || /^\d+$/.test(inputValue)) {
                        setTimeline((prev) => ({
                          ...prev,
                          [key]: inputValue,
                        }));
                      }
                    }}
                    onKeyDown={(e) => {
                      // Block minus, decimal, exponent, and arrow up/down
                      if (
                        e.key === "-" ||
                        e.key === "+" ||
                        e.key === "." ||
                        e.key === "e" ||
                        e.key === "E" ||
                        e.key === "ArrowUp" ||
                        e.key === "ArrowDown"
                      ) {
                        e.preventDefault();
                      }
                    }}
                    onWheel={(e) => e.target.blur()}
                    disabled={isDisabled}
                  />
                </div>
              ))}
            </div>

            {Object.values(timeline).some((val) => val !== "") && (
              <Box
                sx={{
                  marginTop: "16px",
                  padding: "12px",
                  backgroundColor: "#F0F9FF",
                  borderRadius: "8px",
                  border: "1px solid #DBEAFE",
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#0d0d0e",
                  }}
                >
                  Total Estimated Duration
                </div>
                <Typography
                  variant="body2"
                  color="primary"
                  sx={{ fontWeight: "500" }}
                >
                  {calculateTotalWeeks()} weeks
                </Typography>
              </Box>
            )}

            {!isDisabled && !hideSaveButton && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: "16px",
                }}
              >
                <Button
                  variant="contained"
                  onClick={handleSaveTimeline}
                  disabled={isSavingTimeline}
                  sx={{
                    backgroundColor: "#4D6BF6",
                    color: "#fff",
                    borderRadius: "8px",
                    padding: "8px 24px",
                    fontSize: "14px",
                    fontWeight: "500",
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: "#3B5CE6",
                    },
                    "&:disabled": {
                      backgroundColor: "#E5E7EB",
                      color: "#9CA3AF",
                    },
                  }}
                >
                  {isSavingTimeline ? (
                    <>
                      <CircularProgress size={16} sx={{ marginRight: "8px" }} />
                      Saving...
                    </>
                  ) : (
                    "Save"
                  )}
                </Button>
              </Box>
            )}
          </Box>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProposalResponse;
